import IndexedDBService from '../indexedDB';

// Define the Azure team sync data structure
export interface AzureTeamSyncMember {
  id: string;
  name: string;
  email: string;
  isImported: boolean;
}

export interface AzureTeamSyncData {
  id: number;
  name: string;
  members: AzureTeamSyncMember[];
}

// Define the stored data structure with metadata
interface AzureTeamSyncStoredData {
  key: string; // org_id_user_id
  data: AzureTeamSyncData[];
  timestamp: number;
  org_id: string;
  user_id: string;
}

class AzureTeamSyncRepository {
  private dbService: IndexedDBService;
  private readonly STORE_NAME = 'azureTeamSync';
  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

  constructor() {
    this.dbService = new IndexedDBService({
      databaseName: 'CadenceProDB',
      version: 1,
      stores: [
        {
          name: this.STORE_NAME,
          keyPath: 'key',
          indexes: [
            { name: 'org_id', keyPath: 'org_id', unique: false },
            { name: 'user_id', keyPath: 'user_id', unique: false },
            { name: 'timestamp', keyPath: 'timestamp', unique: false }
          ]
        }
      ]
    });
  }

  // Generate unique key for org_id and user_id combination
  private generateKey(orgId: string, userId: string): string {
    return `${orgId}_${userId}`;
  }

  // Check if cached data exists and is still valid (within 15 minutes)
  async isCacheValid(orgId: string, userId: string): Promise<boolean> {
    try {
      const key = this.generateKey(orgId, userId);
      const cachedData = await this.dbService.get<AzureTeamSyncStoredData>(this.STORE_NAME, key);
      
      if (!cachedData) {
        return false;
      }

      const currentTime = Date.now();
      const isValid = (currentTime - cachedData.timestamp) < this.CACHE_DURATION;
      
      return isValid;
    } catch (error) {
      console.error('Error checking cache validity:', error);
      return false;
    }
  }

  // Get cached Azure team sync data
  async getCachedData(orgId: string, userId: string): Promise<AzureTeamSyncData[] | null> {
    try {
      const key = this.generateKey(orgId, userId);
      const cachedData = await this.dbService.get<AzureTeamSyncStoredData>(this.STORE_NAME, key);
      
      if (!cachedData) {
        return null;
      }

      // Check if data is still valid
      const currentTime = Date.now();
      const isValid = (currentTime - cachedData.timestamp) < this.CACHE_DURATION;
      
      if (!isValid) {
        // Data is expired, remove it
        await this.clearCachedData(orgId, userId);
        return null;
      }

      return cachedData.data;
    } catch (error) {
      console.error('Error getting cached data:', error);
      return null;
    }
  }

  // Store Azure team sync data
  async storeCachedData(orgId: string, userId: string, data: AzureTeamSyncData[]): Promise<void> {
    try {
      const key = this.generateKey(orgId, userId);
      
      // First clear any existing data for this org/user
      await this.clearCachedData(orgId, userId);
      
      const storedData: AzureTeamSyncStoredData = {
        key,
        data,
        timestamp: Date.now(),
        org_id: orgId,
        user_id: userId
      };

      await this.dbService.put(this.STORE_NAME, storedData);
    } catch (error) {
      console.error('Error storing cached data:', error);
      throw error;
    }
  }

  // Clear cached data for specific org/user
  async clearCachedData(orgId: string, userId: string): Promise<void> {
    try {
      const key = this.generateKey(orgId, userId);
      await this.dbService.delete(this.STORE_NAME, key);
    } catch (error) {
      console.error('Error clearing cached data:', error);
      // Don't throw error here as it's often called when data doesn't exist
    }
  }

  // Clear all cached data
  async clearAllCachedData(): Promise<void> {
    try {
      await this.dbService.clear(this.STORE_NAME);
    } catch (error) {
      console.error('Error clearing all cached data:', error);
      throw error;
    }
  }

  // Get all cached entries (for debugging or cleanup)
  async getAllCachedEntries(): Promise<AzureTeamSyncStoredData[]> {
    try {
      return await this.dbService.getAll<AzureTeamSyncStoredData>(this.STORE_NAME);
    } catch (error) {
      console.error('Error getting all cached entries:', error);
      return [];
    }
  }

  // Clean up expired entries
  async cleanupExpiredEntries(): Promise<void> {
    try {
      const allEntries = await this.getAllCachedEntries();
      const currentTime = Date.now();

      for (const entry of allEntries) {
        const isExpired = (currentTime - entry.timestamp) >= this.CACHE_DURATION;
        if (isExpired) {
          await this.dbService.delete(this.STORE_NAME, entry.key);
        }
      }
    } catch (error) {
      console.error('Error cleaning up expired entries:', error);
    }
  }

  // Get cache info for debugging
  async getCacheInfo(orgId: string, userId: string): Promise<{
    exists: boolean;
    isValid: boolean;
    timestamp?: number;
    age?: number;
  }> {
    try {
      const key = this.generateKey(orgId, userId);
      const cachedData = await this.dbService.get<AzureTeamSyncStoredData>(this.STORE_NAME, key);
      
      if (!cachedData) {
        return { exists: false, isValid: false };
      }

      const currentTime = Date.now();
      const age = currentTime - cachedData.timestamp;
      const isValid = age < this.CACHE_DURATION;

      return {
        exists: true,
        isValid,
        timestamp: cachedData.timestamp,
        age
      };
    } catch (error) {
      console.error('Error getting cache info:', error);
      return { exists: false, isValid: false };
    }
  }
}

// Export a singleton instance
export const azureTeamSyncRepository = new AzureTeamSyncRepository();
export default azureTeamSyncRepository; 